/* ═══════════════════════════════════════════════════════════════
   VibeAlpha Mobile — API Layer
   REST + SSE Streaming, 复用 Web 后端接口
   ═══════════════════════════════════════════════════════════════ */

import type { StreamEvent, LoginResponse, ChatSession, ChatMessage, SignalTask, TaskLog, Notification } from '@/types'

const BASE = '/api'

function getToken(): string | null {
  return localStorage.getItem('qd_token')
}

function headers(extra?: Record<string, string>): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json', ...extra }
  const token = getToken()
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: headers(opts?.headers as Record<string, string>),
  })
  if (res.status === 401) {
    localStorage.removeItem('qd_token')
    localStorage.removeItem('qd_user')
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `HTTP ${res.status}`)
  }
  const json = await res.json()
  // 后端统一返回 { code, msg, data } 格式，自动解包 data 层
  if (json && typeof json === 'object' && 'code' in json && 'data' in json) {
    if (json.code !== 1) {
      throw new Error(json.msg || '请求失败')
    }
    return json.data as T
  }
  return json as T
}

// ── Auth ──

// 后端部分接口将 role 返回为 { id, permissions } 对象（同 Web 端），
// 移动端统一按字符串消费，因此在入口处做一次归一化，
// 避免对象直接进入 React 渲染规格 (Objects are not valid as a React child)。
function normalizeUser(raw: Record<string, unknown>): import('@/types').User {
  const roleRaw = raw.role
  const role = typeof roleRaw === 'object' && roleRaw !== null
    ? ((roleRaw as { id?: string }).id || undefined)
    : ((roleRaw as string) || undefined)
  return {
    id: raw.id as string,
    username: raw.username as string,
    nickname: (raw.nickname as string) || undefined,
    email: (raw.email as string) || undefined,
    role,
  }
}

export async function login(username: string, password: string): Promise<LoginResponse> {
  const res = await request<{ token: string; userinfo: Record<string, unknown> }>('/user/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })
  return { token: res.token, user: normalizeUser(res.userinfo) }
}

export async function loginBySms(phone: string, smsCode: string): Promise<LoginResponse> {
  const res = await request<{ token: string; userinfo: Record<string, unknown> }>('/user/login', {
    method: 'POST',
    body: JSON.stringify({ phone, sms_code: smsCode }),
  })
  return { token: res.token, user: normalizeUser(res.userinfo) }
}

export async function sendSmsCode(phone: string, purpose = 'sms_login'): Promise<{ ok: boolean; msg?: string }> {
  try {
    await request('/user/send-sms-code', {
      method: 'POST',
      body: JSON.stringify({ phone, purpose }),
    })
    return { ok: true }
  } catch (err) {
    return { ok: false, msg: (err as Error).message || '发送失败' }
  }
}

export async function getUserInfo(): Promise<import('@/types').User> {
  const raw = await request<Record<string, unknown>>('/user/info')
  return normalizeUser(raw)
}

// ── Chat Sessions ──
export async function createChatSession(): Promise<{ session_id: string }> {
  return request('/market/chat/init', { method: 'POST', body: JSON.stringify({}) })
}

export async function getChatSessions(): Promise<ChatSession[]> {
  const res = await request<{ sessions: ChatSession[] }>('/market/chat/sessions', {
    method: 'POST',
    body: JSON.stringify({}),
  })
  return res.sessions || []
}

export async function getChatHistory(sessionId: string): Promise<ChatMessage[]> {
  const res = await request<{ messages: ChatMessage[] }>('/agent/history', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  })
  return res.messages || []
}

export async function deleteChatSession(sessionId: string) {
  return request('/market/chat/sessions/delete', {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  })
}

// ── Chat Stream (SSE) ──
export async function sendChatStream(
  message: string,
  sessionId: string | null,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${BASE}/market/chat`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ message, sessionId }),
    signal,
  })

  if (!res.ok || !res.body) {
    throw new Error(`Stream failed: ${res.status}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed || !trimmed.startsWith('data:')) continue
      const jsonStr = trimmed.slice(5).trim()
      if (jsonStr === '[DONE]') {
        onEvent({ type: 'done' })
        continue
      }
      try {
        const event: StreamEvent = JSON.parse(jsonStr)
        onEvent(event)
      } catch {
        // skip malformed SSE lines
      }
    }
  }
}

// ── Signal Tasks ──
export async function getSignalTasks(filter?: string): Promise<SignalTask[]> {
  const params = filter && filter !== 'all' ? `?is_enabled=${filter}` : ''
  return request(`/signal-tasks${params}`)
}

export async function getSignalTask(taskId: string): Promise<SignalTask> {
  return request(`/signal-tasks/${taskId}`)
}

export async function getTaskLogs(taskId: string, limit = 20): Promise<TaskLog[]> {
  const res = await request<{ logs: TaskLog[] }>(`/signal-tasks/${taskId}/logs?limit=${limit}`)
  return res.logs || []
}

export async function toggleTask(taskId: string): Promise<void> {
  return request(`/signal-tasks/${taskId}/toggle`, { method: 'POST' })
}

export async function triggerTask(taskId: string): Promise<void> {
  return request(`/signal-tasks/${taskId}/trigger`, { method: 'POST' })
}

// ── Notifications ──
export async function getNotifications(limit = 50): Promise<{ notifications: Notification[]; unreadCount: number }> {
  const raw = await request<{ notifications: Array<Record<string, unknown>>; unreadCount: number }>(`/notifications?limit=${limit}`)
  const notifications: Notification[] = (raw.notifications || []).map((n) => ({
    id: n.id as string,
    type: (n.type as Notification['type']) || 'system',
    priority: (n.priority as Notification['priority']) || 'medium',
    category: (n.category as string) || undefined,
    title: (n.title as string) || '',
    message: (n.content as string) || '',
    is_read: !!(n.isRead),
    created_at: (n.timestamp as string) || '',
    action_url: (n.actionUrl as string) || null,
    action_label: (n.actionLabel as string) || null,
    metadata: (n.metadata as Record<string, unknown>) || undefined,
    group_key: (n.groupKey as string) || undefined,
  }))
  return { notifications, unreadCount: raw.unreadCount || 0 }
}

export async function getUnreadCount(): Promise<number> {
  const raw = await request<{ unreadCount: number }>('/notifications/unread-count')
  return Number(raw.unreadCount) || 0
}

export async function markNotificationsRead(ids?: string[]): Promise<void> {
  return request('/notifications/read', {
    method: 'POST',
    body: JSON.stringify(ids && ids.length ? { ids } : {}),
  })
}

export async function deleteNotification(id: string): Promise<void> {
  return request(`/notifications/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function clearNotifications(): Promise<void> {
  return request('/notifications/clear', { method: 'POST' })
}

// ── Market Data (for Briefing) ──
export async function getWatchlist() {
  return request('/market/watchlist/get', { method: 'POST', body: JSON.stringify({}) })
}

export async function getWatchlistPrices(symbols: string[]) {
  return request('/market/watchlist/prices', {
    method: 'POST',
    body: JSON.stringify({ symbols }),
  })
}

export async function getTopListDates(limit = 1) {
  return request(`/market/ashare/top-list/dates?limit=${limit}`)
}

export async function getTopList(tradeDate: string, aggregated = true) {
  return request<{ trade_date: string; aggregated: boolean; count: number; items: import('@/types').TopListItem[] }>('/market/ashare/top-list', {
    method: 'POST',
    body: JSON.stringify({ trade_date: tradeDate, aggregated }),
  })
}

// ── News (中新网) ──
export async function getChinaNews(limit = 30): Promise<import('@/types').NewsItem[]> {
  return request(`/market/news/chinanews?limit=${limit}`)
}

// ── Index Prices (主要指数) ──
export interface IndexQuote {
  symbol: string
  name: string
  price: number | null
  change_pct: number | null
}

const INDEX_LIST: { market: string; symbol: string; name: string }[] = [
  { market: 'AShare', symbol: '000001.SH', name: '上证指数' },
  { market: 'AShare', symbol: '399001.SZ', name: '深证成指' },
  { market: 'AShare', symbol: '399006.SZ', name: '创业板指' },
]

export async function getIndexPrices(): Promise<IndexQuote[]> {
  const results = await Promise.allSettled(
    INDEX_LIST.map(async (idx) => {
      const data = await request<Record<string, unknown>>(
        `/market/price?market=${idx.market}&symbol=${idx.symbol}`,
      )
      return {
        symbol: idx.symbol,
        name: idx.name,
        price: (data?.price as number) ?? null,
        change_pct: (data?.changePercent as number) ?? (data?.change_pct as number) ?? null,
      } as IndexQuote
    }),
  )
  return results
    .filter((r): r is PromiseFulfilledResult<IndexQuote> => r.status === 'fulfilled')
    .map((r) => r.value)
}
